import { Fragment, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import apiClient from "@/api/client";
import {
  payrollActualsApi,
  payrollTheoreticalApi,
} from "@/features/finances/api/finances";
import type {
  MonthlyPayrollActual,
  TheoreticalCompany,
} from "@/features/finances/types";
import { PageLoader } from "@/components/ui/Spinner";
import { formatDate } from "@/utils/formatDate";
import { useAuthStore } from "@/store/auth";

interface Company {
  id: string;
  short_code: string;
  name: string;
}

const TODAY = new Date();
const MONTHS = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

function prevMonth(): { year: number; month: number } {
  const d = new Date(TODAY.getFullYear(), TODAY.getMonth() - 1, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function amtOrEmpty(val: string | undefined): string {
  if (!val || val === "0" || val === "0.00") return "";
  return val;
}

function sumFields(uop: string, uz: string, b2b: string, zus: string, pit: string): string {
  const n = (s: string) => parseFloat(s || "0") || 0;
  return (n(uop) + n(uz) + n(b2b) + n(zus) + n(pit)).toFixed(2);
}

function fmtPln(val: string | number): string {
  const n = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(n)) return "—";
  const [int, dec] = n.toFixed(2).split(".");
  return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, " ")},${dec}`;
}

type DeviationLevel = "ok" | "warn" | "error" | "none";

function deviationLevel(actual: number, theoretical: number): DeviationLevel {
  if (actual === 0 || theoretical === 0) return "none";
  const pct = Math.abs(actual - theoretical) / theoretical;
  if (pct <= 0.15) return "ok";
  if (pct <= 0.35) return "warn";
  return "error";
}

const DEVIATION_BADGE: Record<DeviationLevel, { cls: string; label: string }> =
  {
    ok: {
      cls: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      label: "OK",
    },
    warn: {
      cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
      label: "~OK",
    },
    error: {
      cls: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      label: "BŁĄD",
    },
    none: {
      cls: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
      label: "—",
    },
  };

type RowValues = {
  net_uop: string;
  net_uz: string;
  net_b2b: string;
  zus: string;
  pit: string;
  notes: string;
};

function useCompanies() {
  return useQuery<Company[]>({
    queryKey: ["company-choices"],
    queryFn: () =>
      apiClient
        .get<{ data: Company[] }>("/group-finance/company-choices/")
        .then((r) => r.data.data),
  });
}

function usePayrollActuals(year: number, month: number) {
  return useQuery<MonthlyPayrollActual[]>({
    queryKey: ["payroll-actuals", year, month],
    queryFn: () => payrollActualsApi.list(year, month),
  });
}

function useTheoreticalData(year: number, month: number, enabled: boolean) {
  return useQuery<TheoreticalCompany[]>({
    queryKey: ["payroll-theoretical", year, month],
    queryFn: () => payrollTheoreticalApi.list(year, month),
    enabled,
  });
}

export function PayrollActualsPage() {
  const def = prevMonth();
  const [year, setYear] = useState(def.year);
  const [month, setMonth] = useState(def.month);

  const { user } = useAuthStore();
  const canSeeTheoretical = !!(user?.is_ceo || user?.role === "accountant");

  const qc = useQueryClient();
  const { data: companies = [], isLoading: companiesLoading } = useCompanies();
  const { data: actuals = [], isLoading: actualsLoading } = usePayrollActuals(
    year,
    month,
  );
  const { data: theoretical = [] } = useTheoreticalData(
    year,
    month,
    canSeeTheoretical,
  );

  const [rows, setRows] = useState<Record<string, RowValues>>({});
  const [saveResult, setSaveResult] = useState<{
    ok: boolean;
    msg: string;
  } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const theoreticalByCompany = Object.fromEntries(
    theoretical.map((t) => [t.company, t]),
  );

  const getRow = (companyId: string): RowValues => {
    if (rows[companyId]) return rows[companyId];
    const a = actuals.find((x) => x.company === companyId);
    if (a) {
      return {
        net_uop: amtOrEmpty(a.net_uop),
        net_uz: amtOrEmpty(a.net_uz),
        net_b2b: amtOrEmpty(a.net_b2b),
        zus: amtOrEmpty(a.zus),
        pit: amtOrEmpty(a.pit),
        notes: a.notes ?? "",
      };
    }
    return { net_uop: "", net_uz: "", net_b2b: "", zus: "", pit: "", notes: "" };
  };

  const setField = (
    companyId: string,
    field: keyof RowValues,
    value: string,
  ) => {
    setRows((prev) => ({
      ...prev,
      [companyId]: { ...getRow(companyId), [field]: value },
    }));
    setSaveResult(null);
  };

  const handleMonthChange = (newYear: number, newMonth: number) => {
    setYear(newYear);
    setMonth(newMonth);
    setRows({});
    setSaveResult(null);
    setExpanded(new Set());
  };

  const toggleExpanded = (companyId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const saveAll = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled(
        companies.map((co) => {
          const r = getRow(co.id);
          return payrollActualsApi.upsert({
            company: co.id,
            year,
            month,
            net_uop: r.net_uop || "0",
            net_uz: r.net_uz || "0",
            net_b2b: r.net_b2b || "0",
            zus: r.zus || "0",
            pit: r.pit || "0",
            notes: r.notes || "",
          });
        }),
      );
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length > 0)
        throw new Error(`${failed.length} spółek nie udało się zapisać`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-actuals", year, month] });
      setRows({});
      setSaveResult({
        ok: true,
        msg: "Zapisano urealnienia dla wszystkich spółek.",
      });
    },
    onError: (e: Error) => {
      setSaveResult({ ok: false, msg: e.message });
    },
  });

  const isLoading = companiesLoading || actualsLoading;

  const isLate = TODAY.getDate() > 10;
  const prevM = prevMonth();
  const isPrevMonth = year === prevM.year && month === prevM.month;
  const prevMonthActuals = useQuery<MonthlyPayrollActual[]>({
    queryKey: ["payroll-actuals", prevM.year, prevM.month],
    queryFn: () => payrollActualsApi.list(prevM.year, prevM.month),
    enabled: isLate && !isPrevMonth,
  });
  const prevHasGaps =
    isLate &&
    !isPrevMonth &&
    prevMonthActuals.data != null &&
    prevMonthActuals.data.length < companies.length;

  if (isLoading) return <PageLoader />;

  const yearOptions: number[] = [];
  for (let y = TODAY.getFullYear(); y >= TODAY.getFullYear() - 3; y--)
    yearOptions.push(y);

  const showTheoretical = canSeeTheoretical && theoretical.length > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Urealnienie wypłat
        </h1>

        <div className="flex items-center gap-2">
          <select
            className="border rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 dark:border-gray-600"
            value={month}
            onChange={(e) => handleMonthChange(year, parseInt(e.target.value))}
          >
            {MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
          <select
            className="border rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 dark:border-gray-600"
            value={year}
            onChange={(e) => handleMonthChange(parseInt(e.target.value), month)}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {prevHasGaps && (
        <div className="bg-amber-50 border border-amber-300 text-amber-800 rounded-md px-4 py-3 text-sm dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300">
          Uwaga: minęło 10. dnia miesiąca, a poprzedni miesiąc (
          {MONTHS[prevM.month - 1]} {prevM.year}) ma nieskompletowane
          urealnienia dla części spółek.
        </div>
      )}

      {saveResult && (
        <div
          className={`rounded-md px-4 py-3 text-sm ${
            saveResult.ok
              ? "bg-green-50 border border-green-300 text-green-800 dark:bg-green-900/20 dark:border-green-700 dark:text-green-300"
              : "bg-red-50 border border-red-300 text-red-800 dark:bg-red-900/20 dark:border-red-700 dark:text-red-300"
          }`}
        >
          {saveResult.msg}
        </div>
      )}

      {/* Mobile: card-per-company, tap-to-edit */}
      <div className="sm:hidden space-y-2">
        {companies.map((co) => {
          const r = getRow(co.id);
          const hasActual = actuals.some((a) => a.company === co.id);
          const total = sumFields(r.net_uop, r.net_uz, r.net_b2b, r.zus, r.pit);
          const hasValue = parseFloat(total) > 0;
          const theoreticalEntry = theoreticalByCompany[co.id];
          const theoreticalTotal = theoreticalEntry
            ? parseFloat(theoreticalEntry.theoretical_total)
            : 0;
          const actualTotal = parseFloat(total);
          const devLevel = showTheoretical
            ? deviationLevel(actualTotal, theoreticalTotal)
            : "none";
          const badge = DEVIATION_BADGE[devLevel];
          const isExpanded = expanded.has(co.id);
          const hasEmployees =
            showTheoretical &&
            theoreticalEntry &&
            theoreticalEntry.employees.length > 0;
          const fields = [
            ["net_uop", "Netto UoP"],
            ["net_uz", "Netto UZ"],
            ["net_b2b", "Netto B2B"],
            ["zus", "ZUS łącznie"],
            ["pit", "PIT"],
          ] as const;

          return (
            <div
              key={co.id}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggleExpanded(co.id)}
                className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {co.short_code}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {co.name}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-0.5 shrink-0">
                  <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                    {hasValue ? fmtPln(total) : "—"}
                  </span>
                  {showTheoretical && hasValue && theoreticalTotal > 0 ? (
                    <span
                      className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${badge.cls}`}
                    >
                      {badge.label}
                    </span>
                  ) : (
                    <span
                      className="text-sm leading-none"
                      title={hasActual ? "Urealnienie zapisane" : "Brak urealnienia"}
                    >
                      {hasActual ? "✅" : "⚠️"}
                    </span>
                  )}
                </div>
                <span className="text-gray-400 dark:text-gray-500 shrink-0">
                  {isExpanded ? (
                    <ChevronDown size={18} />
                  ) : (
                    <ChevronRight size={18} />
                  )}
                </span>
              </button>

              {isExpanded && (
                <div className="px-3 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30 space-y-2">
                  {fields.map(([field, label]) => (
                    <div
                      key={field}
                      className="flex items-center justify-between gap-3"
                    >
                      <label className="text-sm text-gray-600 dark:text-gray-400 flex-1">
                        {label}
                      </label>
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        placeholder="0,00"
                        value={r[field]}
                        onChange={(e) => setField(co.id, field, e.target.value)}
                        className="w-32 text-right border rounded px-2 py-2 text-base bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  ))}

                  <div className="pt-2 mt-1 border-t border-gray-200 dark:border-gray-600 flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Razem
                    </span>
                    <span className="font-mono font-semibold text-gray-900 dark:text-gray-100">
                      {hasValue ? fmtPln(total) : "—"}
                    </span>
                  </div>

                  {showTheoretical && (
                    <div
                      className="flex items-center justify-between gap-3"
                      title="Teoria: suma stawek wg umów (EmployeeSalaryCost)"
                    >
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Suma teoretyczna
                      </span>
                      <span className="font-mono text-sm text-gray-500 dark:text-gray-400">
                        {theoreticalEntry
                          ? fmtPln(theoreticalEntry.theoretical_total)
                          : "—"}
                      </span>
                    </div>
                  )}

                  {showTheoretical &&
                    hasValue &&
                    theoreticalTotal > 0 &&
                    (() => {
                      const diff = actualTotal - theoreticalTotal;
                      const pct = (diff / theoreticalTotal) * 100;
                      const cls =
                        diff > 0
                          ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                          : diff < 0
                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400";
                      return (
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Odchylenie
                          </span>
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-xs font-medium ${cls} px-1.5 py-0.5 rounded`}
                            >
                              {diff >= 0 ? "+" : ""}
                              {pct.toFixed(1)}%
                            </span>
                            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                              {diff >= 0 ? "+" : ""}
                              {fmtPln(diff)}
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                  {hasEmployees && (
                    <div className="pt-2 mt-1 border-t border-gray-200 dark:border-gray-600">
                      <div className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 font-medium">
                        Stawki wg umów — {MONTHS[month - 1]} {year}
                      </div>
                      <ul className="space-y-1 text-xs">
                        {theoreticalEntry!.employees.map((emp) => (
                          <li
                            key={emp.employee_id}
                            className="flex items-baseline justify-between gap-2"
                          >
                            <span className="text-gray-700 dark:text-gray-300 truncate flex-1 min-w-0">
                              {emp.employee_name}
                            </span>
                            <span className="font-mono font-semibold text-gray-700 dark:text-gray-300 shrink-0">
                              {fmtPln(emp.monthly_cost)} PLN
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Mobile footer total */}
        <div className="rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-3 py-3 space-y-1">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              Łącznie
            </span>
            <span className="font-mono font-bold text-gray-900 dark:text-gray-100">
              {fmtPln(
                companies.reduce((acc, co) => {
                  const r = getRow(co.id);
                  return (
                    acc +
                    (parseFloat(
                      sumFields(r.net_uop, r.net_uz, r.net_b2b, r.zus, r.pit),
                    ) || 0)
                  );
                }, 0),
              )}
            </span>
          </div>
          {showTheoretical && (
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>Suma teoretyczna</span>
              <span className="font-mono">
                {fmtPln(
                  theoretical.reduce(
                    (acc, t) => acc + (parseFloat(t.theoretical_total) || 0),
                    0,
                  ),
                )}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Desktop/tablet: original table */}
      <div className="hidden sm:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300 w-28">
                Spółka
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">
                Netto UoP
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">
                Netto UZ
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">
                Netto B2B
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">
                ZUS łącznie
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">
                PIT
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">
                Razem
              </th>
              {showTheoretical && (
                <th
                  className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300"
                  title="Teoria: suma stawek wg umów (EmployeeSalaryCost)"
                >
                  Suma teoretyczna
                </th>
              )}
              <th className="px-3 py-2 text-center font-medium text-gray-600 dark:text-gray-300 w-8">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-900">
            {companies.map((co) => {
              const r = getRow(co.id);
              const hasActual = actuals.some((a) => a.company === co.id);
              const total = sumFields(r.net_uop, r.net_uz, r.net_b2b, r.zus, r.pit);
              const hasValue = parseFloat(total) > 0;
              const theoreticalEntry = theoreticalByCompany[co.id];
              const theoreticalTotal = theoreticalEntry
                ? parseFloat(theoreticalEntry.theoretical_total)
                : 0;
              const actualTotal = parseFloat(total);
              const devLevel = showTheoretical
                ? deviationLevel(actualTotal, theoreticalTotal)
                : "none";
              const badge = DEVIATION_BADGE[devLevel];
              const isExpanded = expanded.has(co.id);
              const hasEmployees =
                showTheoretical &&
                theoreticalEntry &&
                theoreticalEntry.employees.length > 0;

              return (
                <Fragment key={co.id}>
                  <tr
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                      <div className="flex items-center gap-1">
                        {hasEmployees && (
                          <button
                            onClick={() => toggleExpanded(co.id)}
                            className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500 flex-shrink-0"
                            title={isExpanded ? "Zwiń" : "Rozwiń per pracownik"}
                          >
                            {isExpanded ? (
                              <ChevronDown size={14} />
                            ) : (
                              <ChevronRight size={14} />
                            )}
                          </button>
                        )}
                        {co.short_code}
                      </div>
                    </td>
                    {(["net_uop", "net_uz", "net_b2b", "zus", "pit"] as const).map(
                      (field) => (
                        <td key={field} className="px-2 py-1">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            value={r[field]}
                            onChange={(e) =>
                              setField(co.id, field, e.target.value)
                            }
                            className="w-28 text-right border rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </td>
                      ),
                    )}
                    <td className="px-3 py-2 text-right font-mono font-semibold text-gray-800 dark:text-gray-200">
                      {hasValue ? fmtPln(total) : "—"}
                    </td>
                    {showTheoretical && (
                      <td className="px-3 py-2 text-right">
                        <div className="flex flex-col items-end gap-1">
                          <span className="font-mono text-gray-500 dark:text-gray-400 text-xs">
                            {theoreticalEntry ? (
                              fmtPln(theoreticalEntry.theoretical_total)
                            ) : (
                              <span className="inline-flex items-center gap-1">
                                <span>—</span>
                                <span
                                  title="Brak skonfigurowanych stawek płac (EmployeeSalaryCost) dla tej spółki w tym miesiącu"
                                  className="cursor-help text-gray-400 dark:text-gray-500"
                                >
                                  ⓘ
                                </span>
                              </span>
                            )}
                          </span>
                          {hasValue && theoreticalTotal > 0 && (
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded font-medium ${badge.cls}`}
                            >
                              {badge.label}
                              {devLevel !== "none" && devLevel !== "ok" && (
                                <>
                                  {" "}
                                  {(
                                    (Math.abs(actualTotal - theoreticalTotal) /
                                      theoreticalTotal) *
                                    100
                                  ).toFixed(0)}
                                  %
                                </>
                              )}
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-3 py-2 text-right text-xs">
                      {showTheoretical && hasValue && theoreticalTotal > 0 ? (
                        <div className="flex flex-col items-end gap-0.5">
                          {(() => {
                            const diff = actualTotal - theoreticalTotal;
                            const pct = (diff / theoreticalTotal) * 100;
                            const cls = diff > 0
                              ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                              : diff < 0
                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400";
                            return (
                              <>
                                <span className={`font-medium ${cls} px-1.5 py-0.5 rounded`}>
                                  {diff >= 0 ? "+" : ""}{pct.toFixed(1)}%
                                </span>
                                <span className="font-mono text-gray-500 dark:text-gray-400">
                                  {diff >= 0 ? "+" : ""}{fmtPln(diff)}
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      ) : (
                        <span
                          className="text-base"
                          title={hasActual ? "Urealnienie zapisane" : "Brak urealnienia"}
                        >
                          {hasActual ? "✅" : "⚠️"}
                        </span>
                      )}
                    </td>
                  </tr>
                  {isExpanded && hasEmployees && (
                    <tr
                      key={`${co.id}-expanded`}
                      className="bg-gray-50/70 dark:bg-gray-800/30"
                    >
                      <td
                        colSpan={showTheoretical ? 9 : 8}
                        className="px-4 py-2"
                      >
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium uppercase tracking-wide">
                          Stawki wg umów — {MONTHS[month - 1]} {year}
                        </div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-gray-400 dark:text-gray-500">
                              <th className="text-left py-0.5 font-medium">
                                Pracownik
                              </th>
                              <th className="text-right py-0.5 font-medium">
                                Stawka/mies.
                              </th>
                              <th className="text-left py-0.5 pl-4 font-medium">
                                Aktywna od
                              </th>
                              <th className="text-left py-0.5 font-medium">
                                do
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                            {theoreticalEntry!.employees.map((emp) => (
                              <tr
                                key={emp.employee_id}
                                className="text-gray-700 dark:text-gray-300"
                              >
                                <td className="py-0.5">{emp.employee_name}</td>
                                <td className="text-right py-0.5 font-mono font-semibold">
                                  {fmtPln(emp.monthly_cost)} PLN
                                </td>
                                <td className="py-0.5 pl-4 text-gray-400 dark:text-gray-500">
                                  {formatDate(emp.effective_from)}
                                </td>
                                <td className="py-0.5 text-gray-400 dark:text-gray-500">
                                  {formatDate(emp.effective_to, "bezterminowo")}
                                </td>
                              </tr>
                            ))}
                            <tr className="border-t border-gray-200 dark:border-gray-600 font-semibold text-gray-800 dark:text-gray-200">
                              <td className="pt-1">Łącznie</td>
                              <td className="text-right pt-1 font-mono">
                                {fmtPln(theoreticalEntry!.theoretical_total)}{" "}
                                PLN
                              </td>
                              <td colSpan={2} />
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
            <tr>
              <td className="px-3 py-2 font-semibold text-gray-900 dark:text-gray-100">
                Łącznie
              </td>
              {(["net_uop", "net_uz", "net_b2b", "zus", "pit"] as const).map((field) => {
                const sum = companies.reduce((acc, co) => {
                  const r = getRow(co.id);
                  return acc + (parseFloat(r[field]) || 0);
                }, 0);
                return (
                  <td key={field} className="px-3 py-2 text-right font-mono font-semibold text-gray-900 dark:text-gray-100">
                    {fmtPln(sum)}
                  </td>
                );
              })}
              <td className="px-3 py-2 text-right font-mono font-bold text-gray-900 dark:text-gray-100">
                {fmtPln(
                  companies.reduce((acc, co) => {
                    const r = getRow(co.id);
                    return acc + (parseFloat(sumFields(r.net_uop, r.net_uz, r.net_b2b, r.zus, r.pit)) || 0);
                  }, 0)
                )}
              </td>
              {showTheoretical && (
                <td className="px-3 py-2 text-right">
                  <span className="font-mono font-bold text-gray-900 dark:text-gray-100">
                    {fmtPln(theoretical.reduce((acc, t) => acc + (parseFloat(t.theoretical_total) || 0), 0))}
                  </span>
                </td>
              )}
              <td className="px-3 py-2 text-right text-xs">
                {showTheoretical && (() => {
                  const footerActual = companies.reduce((acc, co) => {
                    const r = getRow(co.id);
                    return acc + (parseFloat(sumFields(r.net_uop, r.net_uz, r.net_b2b, r.zus, r.pit)) || 0);
                  }, 0);
                  const footerTheoretical = theoretical.reduce(
                    (acc, t) => acc + (parseFloat(t.theoretical_total) || 0), 0
                  );
                  if (footerActual > 0 && footerTheoretical > 0) {
                    const diff = footerActual - footerTheoretical;
                    const pct = (diff / footerTheoretical) * 100;
                    const cls = diff > 0
                      ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                      : diff < 0
                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                        : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400";
                    return (
                      <div className="flex flex-col items-end gap-0.5">
                        <span className={`font-medium ${cls} px-1.5 py-0.5 rounded`}>
                          {diff >= 0 ? "+" : ""}{pct.toFixed(1)}%
                        </span>
                        <span className="font-mono text-gray-500 dark:text-gray-400">
                          {diff >= 0 ? "+" : ""}{fmtPln(diff)}
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => saveAll.mutate()}
          disabled={saveAll.isPending}
          className="w-full sm:w-auto px-4 py-3 sm:py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saveAll.isPending ? "Zapisywanie…" : "Zapisz wszystkie"}
        </button>
      </div>

      <div className="space-y-1">
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Pola: ile firma wypłaca netto pracownikom UoP i UZ, kontrahentom B2B,
          łączny ZUS (obie strony) oraz PIT/zaliczka. Razem = całkowity koszt
          za pracowników i B2B w danym miesiącu.
        </p>
      </div>
    </div>
  );
}
